import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571223641822-b82408a0e705?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjb3BwZXIlMjBvcmUlMjBtaW5pbmd8ZW58MHx8fHwxNzc4OTc0MTE3fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const [stats, setStats] = useState({
    projects: 15,
    regions: 10,
    policyItems: 12,
    commodities: 10,
  });

  useEffect(() => {
    let mounted = true;
    api
      .get("/stats")
      .then((r) => mounted && r.data && setStats((s) => ({ ...s, ...r.data })))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const HERO_STATS = [
    { label: "Mining Projects", value: stats.projects },
    { label: "Regions Covered", value: stats.regions },
    { label: "Policy Items", value: stats.policyItems },
    { label: "Commodities", value: stats.commodities },
  ];
  return (
    <section
      className="border-b border-border bg-white"
      data-testid="hero-section"
    >
      <div className="bb-container py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Left: Editorial copy */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="bb-overline text-bb-copper">
                  Issue 042 · {new Date().toLocaleDateString("en-CA", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                <span className="h-px flex-1 bg-bb-navy/15" />
              </div>
              <h1
                className="bb-headline text-bb-navy text-4xl sm:text-5xl lg:text-6xl xl:text-[5.25rem]"
                data-testid="hero-headline"
              >
                Canada&rsquo;s Mining
                <br />
                <span className="text-bb-copper">Intelligence</span> Brief
              </h1>
              <p
                className="mt-6 text-base lg:text-lg text-bb-slate leading-relaxed max-w-2xl"
                data-testid="hero-subheadline"
              >
                Tracking the projects, policies, commodities, and companies
                shaping Canada&rsquo;s mining sector — from grassroots
                exploration on the Canadian Shield to TSX capital flows and
                Parliament Hill permitting.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#newsletter"
                  className="bb-btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm"
                  data-testid="hero-cta-subscribe"
                >
                  Subscribe to the Brief
                  <ArrowRight size={16} strokeWidth={2.4} />
                </a>
                <a
                  href="#drilldown"
                  className="bb-btn-outline inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm"
                  data-testid="hero-cta-drilldown"
                >
                  Today&rsquo;s Drilldown
                </a>
              </div>
            </div>

            <div className="mt-10 lg:mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
              {HERO_STATS.map((s) => (
                <div
                  key={s.label}
                  className="p-4 lg:p-5 border-b border-r border-border"
                  data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                >
                  <div className="font-mono text-2xl lg:text-3xl text-bb-navy font-semibold">
                    {s.value}
                  </div>
                  <div className="mt-1 bb-overline text-bb-slate">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Hero image card */}
          <div className="lg:col-span-5 relative">
            <div
              className="relative w-full h-full min-h-[320px] lg:min-h-full overflow-hidden border border-border bb-grain"
              data-testid="hero-image-card"
            >
              <img
                src={HERO_IMG}
                alt="Aerial view of a Canadian open-pit mine operation"
                className="bb-img w-full h-full object-cover absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bb-navy/85" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="bb-overline text-white/80 mb-2">
                  Photo Dispatch · British Columbia
                </div>
                <div className="font-archivo text-lg lg:text-xl font-semibold leading-snug">
                  Inside the copper revival: how Highland Valley&rsquo;s
                  extension changes Canada&rsquo;s 2030 supply outlook.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
